/**
 * Comments Page for Accountant Portal
 * Shows all comments made by the accountant across reports
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import AccountantLayout from '../components/AccountantLayout';
import axios from 'axios';
import {
  MessageSquare,
  Search,
  Filter,
  Calendar,
  FileText,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Comment {
  id: string;
  report_id: string;
  report_name: string;
  client_name: string;
  content: string;
  created_at: string;
  section?: string;
  status?: 'pending' | 'resolved' | 'acknowledged';
}

export default function AccountantComments() {
  const navigate = useNavigate();
  const { token, accountant } = useAccountantAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (token) {
      fetchComments();
    }
  }, [token]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      // Try to fetch comments - this endpoint may need to be created
      const response = await axios.get(`${BACKEND_URL}/api/v1/collaboration/accountant/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (err: any) {
      console.error('Error fetching comments:', err);
      // If endpoint doesn't exist yet, show empty state instead of error
      if (err.response?.status === 404) {
        setComments([]);
      } else {
        setError(err.response?.data?.detail || 'Failed to load comments');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const filteredComments = comments.filter(comment => {
    const matchesSearch = 
      comment.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.report_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || comment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'acknowledged':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status?: string) => {
    const styles: Record<string, string> = {
      resolved: 'bg-green-100 text-green-700',
      acknowledged: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700'
    };
    return styles[status || 'pending'] || styles.pending;
  };

  if (!accountant) {
    navigate('/accountant/login');
    return null;
  }

  return (
    <AccountantLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Comments</h1>
            <p className="text-gray-500 mt-1">Comments and notes across all reports</p>
          </div>
          <button
            onClick={fetchComments}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading comments...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={fetchComments}
              className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
            >
              Try Again
            </button>
          </div>
        ) : filteredComments.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Comments Yet</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Your comments on reports will appear here'}
            </p>
            <button
              onClick={() => navigate('/accountant/reports')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <FileText className="w-4 h-4" />
              View Reports
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredComments.map((comment) => (
              <div 
                key={comment.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/accountant/reports/${comment.report_id}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">{comment.report_name || 'Report'}</p>
                        {comment.section && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {comment.section}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{comment.content}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(comment.created_at)}
                        </span>
                        <span>{comment.client_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(comment.status)}`}>
                      {getStatusIcon(comment.status)}
                      {comment.status || 'pending'}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {!loading && filteredComments.length > 0 && (
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing {filteredComments.length} of {comments.length} comments
          </div>
        )}
      </div>
    </AccountantLayout>
  );
}
